import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CheckCircle as ApproveIcon, Warning as WarningIcon } from '@mui/icons-material';
import { commissionApi } from '../../services/api';

const REASON_SUGGESTIONS = [
  'Verified product match',
  'Customer confirmed purchase',
  'Manual review approved',
  'SKU match verified',
  'Same product, different variant',
];

export default function ApprovalConfirmDialog({ open, item, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [cookieData, setCookieData] = useState(null);
  const [cookieLoading, setCookieLoading] = useState(false);

  useEffect(() => {
    const fetchCookieData = async () => {
      if (!item?.id || !open) return;

      try {
        setCookieLoading(true);
        const response = await commissionApi.getCookieData(item.id);
        if (response.success) {
          setCookieData(response.cookie);
        }
      } catch (err) {
        console.error('Failed to fetch cookie data:', err);
      } finally {
        setCookieLoading(false);
      }
    };

    if (open) {
      setReason('');
      setSelectedSuggestion('');
      setError(null);
      setResult(null);
      fetchCookieData();
    }
  }, [item?.id, open]);

  const handleSuggestionChange = (event) => {
    const value = event.target.value;
    setSelectedSuggestion(value);
    if (value) {
      setReason(value);
    }
  };

  const handleApprove = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for approval');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await commissionApi.approve(item.id, { reason: reason.trim() });

      if (response.success) {
        setResult(response);
      } else {
        // Handle specific error types
        if (response.error === 'cookie_already_used') {
          setError(response.message || 'Cookie has already been used for another approval');
        } else if (response.error === 'order_item_already_approved') {
          setError(response.message || 'Order item already has an approved commission');
        } else {
          setError(response.error || 'Failed to approve');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onSuccess();
    } else {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApproveIcon color="success" />
          Approve Commission
        </Box>
      </DialogTitle>

      <DialogContent>
        {result ? (
          // Success state
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ApproveIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Commission Approved
            </Typography>
            {result.cascade_declined_count > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {result.cascade_declined_count} other pending item(s) were automatically declined
              </Alert>
            )}
          </Box>
        ) : (
          <>
            {/* Cookie warning */}
            {cookieLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : cookieData?.already_approved ? (
              <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Cookie Already Used
                </Typography>
                <Typography variant="body2">
                  This cookie has already been approved for order{' '}
                  {cookieData.existing_approval?.order_number}. Approving this item will fail.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to approve this commission. This action cannot be undone.
              </Alert>
            )}

            {/* Item summary */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Order: {item?.order_number || '-'}
              </Typography>
              <Typography variant="body2">
                {item?.product_name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Commission: {item?.commission_amount?.toFixed(2) || '-'}
              </Typography>
            </Box>

            {/* Reason selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select a reason (optional)</InputLabel>
              <Select
                value={selectedSuggestion}
                onChange={handleSuggestionChange}
                label="Select a reason (optional)"
              >
                <MenuItem value="">Custom reason</MenuItem>
                {REASON_SUGGESTIONS.map((suggestion) => (
                  <MenuItem key={suggestion} value={suggestion}>
                    {suggestion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Reason input */}
            <TextField
              label="Reason for approval"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
              error={!!error && !reason.trim()}
              helperText={!reason.trim() && error ? 'Reason is required' : ''}
              placeholder="Enter the reason for approving this commission..."
            />

            {error && reason.trim() && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {result ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              color="success"
              variant="contained"
              disabled={loading || !reason.trim() || cookieData?.already_approved}
              startIcon={loading ? <CircularProgress size={20} /> : <ApproveIcon />}
            >
              {loading ? 'Approving...' : 'Approve'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
